import {
  playerManifestSchema,
  type HeartbeatInput,
  type HeartbeatResponse,
  type PlayerManifest,
} from '@signage/shared';
import type { HttpAdapter, LoggerAdapter } from './adapters.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 401/403 mean the pairing is gone; the player must stop and show the pairing screen. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export interface PairingRequestResult {
  code: string;
  pollToken: string;
  expiresAt: string;
  pollIntervalSec: number;
}

export type PairingStatus =
  | { status: 'pending'; expiresAt: string }
  | { status: 'expired' }
  | {
      status: 'paired';
      deviceToken: string;
      screenId: string;
      screenName: string;
      organizationId: string;
      organizationName: string;
      apiBaseUrl?: string;
    };

export interface DeviceDescriptor {
  platform: string;
  osVersion?: string | null;
  model?: string | null;
  arch?: string | null;
  playerVersion: string;
  hardwareId?: string | null;
  width?: number;
  height?: number;
  orientation?: 'landscape' | 'portrait';
}

/**
 * Thin, typed client for the device-facing API.
 *
 * Every call has a timeout and surfaces failures as ApiError so the sync engine can decide
 * whether to retry, back off, or stop. Nothing here throws in a way that could take down
 * playback: callers treat network failure as "keep playing what we have".
 */
export class PlayerApiClient {
  constructor(
    private readonly http: HttpAdapter,
    private baseUrl: string,
    private deviceToken: string | null,
    private readonly logger: LoggerAdapter,
  ) {}

  setBaseUrl(url: string): void {
    this.baseUrl = url.replace(/\/$/, '');
  }

  /** The address currently in use, without a trailing slash. */
  get currentBaseUrl(): string {
    return this.baseUrl.replace(/\/$/, '');
  }

  setDeviceToken(token: string | null): void {
    this.deviceToken = token;
  }

  get apiRoot(): string {
    return `${this.baseUrl.replace(/\/$/, '')}/api/v1`;
  }

  private async call<T>(
    path: string,
    options: {
      method?: string;
      body?: unknown;
      auth?: boolean;
      timeoutMs?: number;
      rawBody?: Uint8Array;
      contentType?: string;
    } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (options.auth !== false) {
      if (!this.deviceToken) throw new ApiError(401, 'This device is not paired');
      headers.authorization = `Bearer ${this.deviceToken}`;
    }
    let body: string | Uint8Array | undefined;
    if (options.rawBody) {
      body = options.rawBody;
      headers['content-type'] = options.contentType ?? 'application/octet-stream';
    } else if (options.body !== undefined) {
      body = JSON.stringify(options.body);
      headers['content-type'] = 'application/json';
    }

    const response = await this.http.request(`${this.apiRoot}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body,
      timeoutMs: options.timeoutMs ?? 20_000,
    });

    if (!response.ok) {
      let message = `Request failed with status ${response.status}`;
      let code: string | undefined;
      try {
        const payload = await response.json<{ error?: { message?: string; code?: string } }>();
        message = payload.error?.message ?? message;
        code = payload.error?.code;
      } catch {
        /* keep the generic message */
      }
      throw new ApiError(response.status, message, code);
    }

    return response.json<T>();
  }

  /* ------------------------------------------------------------------ pairing */

  async requestPairing(device: DeviceDescriptor): Promise<PairingRequestResult> {
    return this.call<PairingRequestResult>('/player/pair/request', {
      method: 'POST',
      auth: false,
      body: { device },
    });
  }

  async pairingStatus(pollToken: string): Promise<PairingStatus> {
    return this.call<PairingStatus>(
      `/player/pair/status?pollToken=${encodeURIComponent(pollToken)}`,
      { auth: false },
    );
  }

  /* ---------------------------------------------------------------- lifecycle */

  async heartbeat(input: HeartbeatInput): Promise<HeartbeatResponse> {
    return this.call<HeartbeatResponse>('/player/heartbeat', {
      method: 'POST',
      body: input,
      timeoutMs: 15_000,
    });
  }

  async contentVersion(): Promise<string> {
    const result = await this.call<{ contentVersion: string }>('/player/version', {
      timeoutMs: 10_000,
    });
    return result.contentVersion;
  }

  async manifest(): Promise<PlayerManifest> {
    const raw = await this.call<unknown>('/player/manifest', { timeoutMs: 60_000 });
    const parsed = playerManifestSchema.safeParse(raw);
    if (!parsed.success) {
      // A manifest we cannot understand must never replace a working one.
      this.logger.error('Manifest failed validation', {
        issues: parsed.error.issues.slice(0, 5),
      });
      throw new ApiError(500, 'The server returned a manifest this player cannot read');
    }
    return parsed.data;
  }

  async reportSyncStatus(input: {
    state: 'idle' | 'syncing' | 'synced' | 'failed';
    contentVersion?: string | null;
    activeContentVersion?: string | null;
    filesTotal?: number;
    filesCompleted?: number;
    bytesTotal?: number;
    bytesCompleted?: number;
    message?: string | null;
    errors?: { mediaId?: string; message: string }[];
  }): Promise<void> {
    await this.call('/player/sync-status', { method: 'POST', body: input });
  }

  async uploadProofOfPlay(
    events: {
      eventId: string;
      mediaId?: string | null;
      playlistId?: string | null;
      slideshowId?: string | null;
      scheduleId?: string | null;
      startedAt: string;
      completedAt: string;
      durationMs: number;
      completed: boolean;
    }[],
  ): Promise<{ accepted: number }> {
    return this.call<{ ok: true; accepted: number }>('/player/proof-of-play', {
      method: 'POST',
      body: { events },
      timeoutMs: 30_000,
    });
  }

  async uploadEvents(
    events: {
      eventId: string;
      level: string;
      code: string;
      message: string;
      context?: unknown;
      occurredAt: string;
    }[],
  ): Promise<{ accepted: number }> {
    return this.call<{ ok: true; accepted: number }>('/player/errors', {
      method: 'POST',
      body: { events },
    });
  }

  async acknowledgeCommand(
    commandId: string,
    status: 'done' | 'failed',
    message?: string,
  ): Promise<void> {
    await this.call('/player/commands/ack', {
      method: 'POST',
      body: { commandId, status, message: message ?? null },
    });
  }

  async uploadScreenshot(
    jpeg: Uint8Array,
    dimensions: { width?: number; height?: number } = {},
  ): Promise<{ url: string }> {
    const headers: Record<string, string> = {};
    if (dimensions.width) headers['x-screenshot-width'] = String(dimensions.width);
    if (dimensions.height) headers['x-screenshot-height'] = String(dimensions.height);
    if (!this.deviceToken) throw new ApiError(401, 'This device is not paired');

    const response = await this.http.request(`${this.apiRoot}/player/screenshot`, {
      method: 'POST',
      headers: {
        ...headers,
        authorization: `Bearer ${this.deviceToken}`,
        'content-type': 'image/jpeg',
      },
      body: jpeg,
      timeoutMs: 60_000,
    });
    if (!response.ok) {
      throw new ApiError(response.status, `Screenshot upload failed (${response.status})`);
    }
    return response.json<{ url: string }>();
  }

  /** Streaming download used by the download manager; supports Range resume. */
  async downloadRange(
    url: string,
    startByte: number,
  ): Promise<{
    status: number;
    ok: boolean;
    supportsResume: boolean;
    totalBytes: number | null;
    body: AsyncIterable<Uint8Array> | null;
  }> {
    const headers: Record<string, string> = {};
    if (startByte > 0) headers.range = `bytes=${startByte}-`;
    const response = await this.http.request(url, {
      method: 'GET',
      headers,
      stream: true,
      timeoutMs: 0,
    });
    const contentRange = response.headers['content-range'];
    const contentLength = response.headers['content-length'];
    let totalBytes: number | null = null;
    if (contentRange) {
      const match = /\/(\d+)$/.exec(contentRange);
      if (match) totalBytes = Number(match[1]);
    } else if (contentLength) {
      totalBytes = Number(contentLength);
    }
    return {
      status: response.status,
      ok: response.ok,
      supportsResume: response.status === 206,
      totalBytes,
      body: response.body,
    };
  }
}
